import {
  IconClockDollar,
  IconClockExclamation,
  IconClockHour7,
  IconPercentage,
  IconTipJar,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ContentCard,
  Leaderboard,
  MetricCard,
  ScalrBoxView,
  ScalrCarousel,
} from '@/components/customComponents'
import { MasterChart } from '@/components/ui/charts'
import KPIGoalChart from '@/components/ui/charts/KpiGoalChart'
import { DataTable } from '@/components/ui/data-table'

import {
  pieChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import { metricCardColors } from '@/constants/COLOR_PALETTE'
import {
  toast,
  useLaborMetrics,
  useLaborRegionBoxMetrics,
  useLaborTableMetrics,
} from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { KpiGoalsModel } from '@/types/clarity'
import { dateToString, groupedLaborMetricsColumns } from '@/utils'
import { DateType } from '@/utils/date'

export default function LaborPage({
  kpiGoals,
  kpiGoalsLoading,
}: {
  kpiGoals: KpiGoalsModel | undefined
  kpiGoalsLoading: boolean
}) {
  const { t } = useTranslation()
  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter('labor')
  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('labor'))

  const [isViewMore, setViewMore] = useState(false)
  const {
    branchData,
    companyData,
    metricCardData,
    loading,
    error: laborMetricsError,
    refetch,
  } = useLaborMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType as DateType,
    selectedBranch
  )

  const {
    processedData,
    loading: regionCardsLoading,
    error: regionCardsError,
    refetch: regionCardsRefetch,
  } = useLaborRegionBoxMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType as DateType
  )

  const {
    laborTableData,
    laborTableColumns,
    loading: laborTableLoading,
    error: laborTableError,
  } = useLaborTableMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing labor page data...')
      refetch()
      regionCardsRefetch()
    }
  }, [refreshTrigger, refetch, regionCardsRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (laborMetricsError || regionCardsError || laborTableError) {
      toast({
        title: t('clarity.errors.errorFetchingLabor'),
        description:
          laborMetricsError?.message ||
          regionCardsError?.message ||
          laborTableError?.message,
        variant: 'destructive',
      })
    }
  }, [laborMetricsError, regionCardsError, laborTableError])

  const handleViewMore = () => {
    setViewMore(!isViewMore)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <ScalrCarousel>
          <MetricCard
            title={t('clarity.metrics.laborRatio')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.laborRatio}
            ytdCurrContent={metricCardData?.currMetricsYearly?.laborRatio}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.laborRatio}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.laborRatio}
            content={companyData?.laborRatio}
            type="percentage"
            kpiGoal={kpiGoals?.laborRatio}
            loading={loading || kpiGoalsLoading}
            icon={<IconPercentage color="white" size={20} />}
            color={metricCardColors.blue500}
            isIncreasingPositive={false}
          />
          <MetricCard
            title={t('clarity.metrics.laborHours')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.laborHours}
            ytdCurrContent={metricCardData?.currMetricsYearly?.laborHours}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.laborHours}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.laborHours}
            content={companyData?.laborHours}
            type="hour"
            kpiGoal={kpiGoals?.laborHours}
            loading={loading || kpiGoalsLoading}
            icon={<IconClockHour7 color="white" size={20} />}
            color={metricCardColors.red500}
            isIncreasingPositive={false}
          />
          <MetricCard
            title={t('clarity.metrics.wagePerHour')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.unitLaborCost}
            ytdCurrContent={metricCardData?.currMetricsYearly?.unitLaborCost}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.unitLaborCost}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.unitLaborCost}
            content={companyData?.unitLaborCost}
            type="money"
            kpiGoal={kpiGoals?.wagePerHour}
            loading={loading || kpiGoalsLoading}
            icon={<IconClockDollar color="white" size={20} />}
            color={metricCardColors.green500}
            isIncreasingPositive={false}
          />
          <MetricCard
            title={t('clarity.metrics.overtime')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.overtimePercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.overtimePercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.overtimePercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.overtimePercentage
            }
            content={companyData?.overtimePercentage}
            type="percentage"
            kpiGoal={kpiGoals?.overtime}
            loading={loading || kpiGoalsLoading}
            icon={<IconClockExclamation color="white" size={20} />}
            color={metricCardColors.orange500}
            isIncreasingPositive={false}
          />
          <MetricCard
            title={t('clarity.metrics.tips')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.tipPercentage}
            ytdCurrContent={metricCardData?.currMetricsYearly?.tipPercentage}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.tipPercentage}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.tipPercentage}
            content={companyData?.tipPercentage}
            type="percentage"
            kpiGoal={kpiGoals?.tips}
            loading={loading || kpiGoalsLoading}
            icon={<IconTipJar color="white" size={20} />}
            color={metricCardColors.blue500}
          />
        </ScalrCarousel>
      </div>

      {isViewMore ? (
        <DataTable
          columns={groupedLaborMetricsColumns}
          data={companyData ? [companyData, ...branchData] : []}
          selectedBranch={selectedBranch}
          cardTitle={t('clarity.cards.laborMetrics')}
          cardDescription={t('clarity.cards.laborMetricsDescription')}
          loading={loading}
          onClose={handleViewMore}
          tableViewOption
        />
      ) : (
        <>
          <Leaderboard
            title={t('clarity.cards.storeLeaderboard')}
            data={laborTableData}
            columns={laborTableColumns}
            loading={laborTableLoading}
            showBadge={false}
            sortBy="hour"
            enablePagination
          />

          <ScalrBoxView
            data={processedData}
            loading={regionCardsLoading}
            filterValues={{
              start: dateToString(date, 'from'),
              end: dateToString(date, 'to'),
            }}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ContentCard
              title={t('clarity.cards.tipsByShifts')}
              loading={loading}
              isEmpty={!companyData?.chartsData.tipsByShiftsRaw}
            >
              {companyData?.chartsData.tipsByShiftsRaw && (
                <ReactEcharts
                  option={stackedBarChartOptions(
                    companyData?.chartsData.tipsByShiftsRaw,
                    ['Regular', 'Tip Top-up'],
                    ['Early AM', 'AM', 'MID', 'PM'],
                    '60%',
                    'money'
                  )}
                />
              )}
            </ContentCard>
            <ContentCard
              title={t('clarity.cards.hoursByType')}
              loading={loading}
              isEmpty={!companyData?.chartsData.hoursByTypeRaw}
            >
              {companyData && (
                <ReactEcharts
                  option={getPieChartData(
                    companyData.chartsData.hoursByTypeRaw
                  )}
                />
              )}
            </ContentCard>

            <ContentCard
              title={t('clarity.cards.laborRatioVsKpiGoal')}
              loading={loading || kpiGoalsLoading}
            >
              <KPIGoalChart
                trailingGoalData={28}
                currentData={companyData?.laborRatio || 0}
                kpiGoalData={kpiGoals?.laborRatio || 0}
                unit="percentage"
              />
            </ContentCard>
          </div>
        </>
      )}

      <MasterChart
        chartType="labor"
        dateRange={date}
        selectedBranchFilter={selectedBranch}
        chartTitle={t('clarity.charts.laborChart')}
      />
    </div>
  )
}

// TODO: to be moved to a separate file
const getStackedBarChartData = (data: number[][]) => {
  return stackedBarChartOptions(
    data,
    ['Regular', 'Tip Top-up'],
    ['Early AM', 'AM', 'MID', 'PM']
  )
}

const getPieChartData = (hoursByTypeRaw: {
  manager: number
  barista: number
  training: number
  marketing: number
}) => {
  const data = [
    hoursByTypeRaw.manager,
    hoursByTypeRaw.barista,
    hoursByTypeRaw.training,
    hoursByTypeRaw.marketing,
  ]

  return pieChartOptions(data, ['Manager', 'Barista', 'Training', 'Marketing'])
}
